import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  buildStxEtxAsciiLenPacket,
  convertEn,
  drainA1Frames,
  getFormattedDate,
  INT_MAX,
  parseSpSnType1Step2,
  setActionLog,
  startReading,
  startSerialRxMonitor,
  stopSerialRxMonitor,
} from "../utils/util";
import { useCanWrite } from "../utils/useCanWrite";
import {
  Box,
  ActionIcon,
  Button,
  Checkbox,
  Flex,
  Paper,
  Text,
  TextInput,
  Badge,
  Modal,
  SegmentedControl,
  Select,
} from "@mantine/core";
import {
  IconChevronLeft,
  IconChevronRight,
  IconSearch,
  IconAlertTriangle,
} from "@tabler/icons-react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import classes from "./ScsKeyInject.module.css";
import { useWorkEnvironment } from "../contexts/WorkEnvironmentContext";
import {
  WORK_ENV_MODEL_MISMATCH_MSG,
  applyWorkEnvironmentToParams,
  isWorkEnvironmentModelMismatch,
} from "../utils/injectWorkEnvironment";

type SendTarget = "serial" | "mac";

const AUTO_CLOSE_AFTER_INJECT_KEY = "scsKeyInject.autoCloseAfterInject";

const readAutoCloseAfterInjectPref = () => {
  try {
    return localStorage.getItem(AUTO_CLOSE_AFTER_INJECT_KEY) === "1";
  } catch {
    return false;
  }
};

interface TerminalInjectInfo {
  terminalId: number;
  serialNo: string | null;
  spSerialNo: string;
  macAddress: string | null;
  internalModelName: string | null;
  externalModelName: string | null;
  spSerialInjectionYn: string;
  macInjectionYn: string;
  dikYn?: string;
  securityKeyRef1?: string | null;
}

/** GET /api/v1/terminal/list 응답 행 */
interface TerminalListRow {
  terminalId: number;
  serialNo?: string | null;
  spSerialNo: string;
  macAddress?: string | null;
  internalModelName?: string | null;
  externalModelName?: string | null;
  spSerialInjectionYn?: string;
  macInjectionYn?: string;
  terminalModelId?: number;
  dikYn?: string;
  securityKeyRef1?: string | null;
}

interface TerminalGroupOption {
  terminalGroupId: number;
  aliasName: string;
}

const mapListRowToInjectInfo = (row: TerminalListRow): TerminalInjectInfo => ({
  terminalId: row.terminalId,
  serialNo: row.serialNo ?? null,
  spSerialNo: row.spSerialNo ?? "",
  macAddress: row.macAddress ?? null,
  internalModelName: row.internalModelName ?? null,
  externalModelName: row.externalModelName ?? null,
  spSerialInjectionYn: row.spSerialInjectionYn ?? "N",
  macInjectionYn: row.macInjectionYn ?? "N",
  dikYn: row.dikYn,
  securityKeyRef1: row.securityKeyRef1 ?? null,
});

type LogGroupType = "connect" | "serial" | "mac" | "system";
type LogTone = "default" | "ok" | "err" | "detail";

interface LogLine {
  time: string;
  text: string;
  tone: LogTone;
}

interface LogGroup {
  id: number;
  type: LogGroupType;
  title: string;
  time: string;
  lines: LogLine[];
}

const GROUP_STYLE: Record<
  LogGroupType,
  { border: string; bg: string; titleColor: string }
> = {
  connect: { border: "#3b82f6", bg: "#eff6ff", titleColor: "#1d4ed8" },
  serial: { border: "#0d9488", bg: "#f0fdfa", titleColor: "#0f766e" },
  mac: { border: "#7c3aed", bg: "#f5f3ff", titleColor: "#6d28d9" },
  system: { border: "#94a3b8", bg: "#f8fafc", titleColor: "#475569" },
};

const bytesToHex = (bytes: number[] | Uint8Array): string =>
  Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join(" ");

const isInjected = (yn: string | null | undefined) =>
  (yn ?? "").trim().toUpperCase() === "Y";

const isInjectWorkComplete = (row: {
  spSerialInjectionYn?: string;
  macInjectionYn?: string;
}) => isInjected(row.spSerialInjectionYn) && isInjected(row.macInjectionYn);

/** 단말 주입 순서: 맥(2번) → 시리얼(3번) */
const resolveNextInjectType = (
  row: Pick<TerminalInjectInfo, "spSerialInjectionYn" | "macInjectionYn">,
): SendTarget | null => {
  if (!isInjected(row.macInjectionYn)) return "mac";
  if (!isInjected(row.spSerialInjectionYn)) return "serial";
  return null;
};

/** 조회 기준 앵커 + 범위 목록 (중복 제거). 현재 작업 S/N이 앵커와 달라도 목록에서 빠지지 않음 */
const buildDisplayQueue = (
  anchor: TerminalListRow | null,
  rangeList: TerminalListRow[],
): TerminalListRow[] => {
  const out: TerminalListRow[] = [];
  const seen = new Set<string>();
  if (anchor?.spSerialNo?.trim()) {
    out.push(anchor);
    seen.add(anchor.spSerialNo.trim());
  }
  for (const row of rangeList) {
    const sn = (row.spSerialNo || "").trim();
    if (!sn || seen.has(sn)) continue;
    out.push(row);
    seen.add(sn);
  }
  return out;
};

/** 화살표 이동용 (관련 S/N 전체). 자동 다음 주입은 미완료만 사용 */
const buildNavQueue = (
  anchor: TerminalListRow | null,
  rangeList: TerminalListRow[],
): TerminalListRow[] => buildDisplayQueue(anchor, rangeList);

/** 현재 작업 단말이 큐에 없으면 합성해 넣음 (주입 목록에 항상 표시) */
const ensureCurrentInQueue = (
  rows: TerminalListRow[],
  current: TerminalInjectInfo | null,
): TerminalListRow[] => {
  if (!current?.spSerialNo?.trim()) return rows;
  const sn = current.spSerialNo.trim();
  if (rows.some((r) => r.spSerialNo === sn)) return rows;
  const synthetic: TerminalListRow = {
    terminalId: current.terminalId,
    serialNo: current.serialNo,
    spSerialNo: sn,
    macAddress: current.macAddress,
    internalModelName: current.internalModelName,
    externalModelName: current.externalModelName,
    spSerialInjectionYn: current.spSerialInjectionYn,
    macInjectionYn: current.macInjectionYn,
  };
  return [...rows, synthetic];
};

const countPendingInject = (rows: TerminalListRow[]) =>
  rows.filter((row) => !isInjectWorkComplete(row)).length;

const findNextIncompleteRow = (
  ordered: TerminalListRow[],
  afterSn: string | null,
): TerminalListRow | null => {
  if (!ordered.length) return null;
  const startIdx = afterSn
    ? ordered.findIndex((row) => row.spSerialNo === afterSn)
    : -1;
  for (let i = Math.max(0, startIdx + 1); i < ordered.length; i++) {
    if (!isInjectWorkComplete(ordered[i])) return ordered[i];
  }
  return null;
};

const parseSerialSequence = (value: string) => {
  const match = value.trim().match(/^(.*?)(\d+)$/);
  if (!match) return null;
  return {
    prefix: match[1],
    numberText: match[2],
    number: Number(match[2]),
  };
};

const compareSerialSequence = (a: string, b: string) => {
  const parsedA = parseSerialSequence(a);
  const parsedB = parseSerialSequence(b);
  if (
    parsedA &&
    parsedB &&
    parsedA.prefix === parsedB.prefix &&
    parsedA.numberText.length === parsedB.numberText.length
  ) {
    return parsedA.number - parsedB.number;
  }
  return a.localeCompare(b);
};

const sortSerialRows = (rows: TerminalListRow[], descending: boolean) =>
  [...rows].sort((a, b) => {
    const order = compareSerialSequence(a.spSerialNo, b.spSerialNo);
    return descending ? -order : order;
  });

const formatSerialNumber = (
  prefix: string,
  number: number,
  numberLength: number,
) => `${prefix}${String(number).padStart(numberLength, "0")}`;

/** previous ↔ current 사이 중간 시리얼 목록 (없으면 null, 연속이면 빈 배열이 아닌 null 처리용 empty 아님) */
const listIntermediateSerials = (
  previous: string,
  current: string,
): string[] | null => {
  const parsedPrev = parseSerialSequence(previous);
  const parsedCurrent = parseSerialSequence(current);
  if (
    !parsedPrev ||
    !parsedCurrent ||
    parsedPrev.prefix !== parsedCurrent.prefix ||
    parsedPrev.numberText.length !== parsedCurrent.numberText.length
  ) {
    return null;
  }
  const diff = Math.abs(parsedCurrent.number - parsedPrev.number);
  if (diff <= 1) return [];

  const start = Math.min(parsedPrev.number, parsedCurrent.number) + 1;
  const end = Math.max(parsedPrev.number, parsedCurrent.number) - 1;
  const out: string[] = [];
  for (let n = start; n <= end; n++) {
    out.push(
      formatSerialNumber(parsedPrev.prefix, n, parsedPrev.numberText.length),
    );
  }
  return out;
};

const formatSerialListLabel = (sns: string[]) => {
  if (!sns.length) return "";
  if (sns.length === 1) return sns[0];
  if (sns.length === 2) return `${sns[0]}, ${sns[1]}`;
  return `${sns[0]} ~ ${sns[sns.length - 1]} (${sns.length}건)`;
};

type SequenceSkipKind = "missing" | "already_done" | "mixed";

interface SequenceSkipNotice {
  kind: SequenceSkipKind;
  title: string;
  message: string;
}

/**
 * 시리얼 번호가 건너뛸 때:
 * - 목록에 있고 주입완료 → 이미 완료
 * - 목록에 없음 → 중간 번호 누락
 */
const buildSequenceSkipNotice = (
  previous: string,
  current: string,
  knownRows: TerminalListRow[],
): SequenceSkipNotice | null => {
  const mids = listIntermediateSerials(previous, current);
  if (mids == null || mids.length === 0) return null;

  const bySn = new Map(
    knownRows
      .filter((r) => r.spSerialNo?.trim())
      .map((r) => [r.spSerialNo.trim(), r] as const),
  );

  const done: string[] = [];
  const missing: string[] = [];
  const incomplete: string[] = [];

  for (const sn of mids) {
    const row = bySn.get(sn);
    if (!row) missing.push(sn);
    else if (isInjectWorkComplete(row)) done.push(sn);
    else incomplete.push(sn);
  }

  // 중간 번호가 모두 목록에 있고 주입완료 → 누락이 아님
  if (missing.length === 0 && incomplete.length === 0 && done.length > 0) {
    const label = formatSerialListLabel(done);
    return {
      kind: "already_done",
      title: "이미 주입 완료",
      message:
        done.length === 1
          ? `이미 주입 완료: ${label}`
          : `이미 주입 완료(건너뜀): ${label}`,
    };
  }

  if (done.length === 0 && incomplete.length === 0 && missing.length > 0) {
    const label = formatSerialListLabel(missing);
    return {
      kind: "missing",
      title: "중간 번호 누락",
      message:
        missing.length === 1
          ? `중간 번호 누락: ${label}`
          : `중간 번호 누락: ${label}`,
    };
  }

  const parts: string[] = [];
  if (done.length) parts.push(`이미 완료 ${formatSerialListLabel(done)}`);
  if (missing.length) parts.push(`누락 ${formatSerialListLabel(missing)}`);
  if (incomplete.length) {
    parts.push(`미완료 ${formatSerialListLabel(incomplete)}`);
  }
  return {
    kind: "mixed",
    title: "시리얼 번호 확인",
    message: parts.join(" · "),
  };
};

const filterRowsByPrefix = (rows: TerminalListRow[], prefix: string) => {
  const p = prefix.trim().toUpperCase();
  if (!p) return rows;
  return rows.filter((row) =>
    (row.spSerialNo || "").toUpperCase().startsWith(p),
  );
};

/** 화면 표시용 MAC (전송값에는 사용하지 않음)
 * - C4E7BE0A5D8E → C4E7BE 0A5D8E (12자리 hex는 6+6)
 * - AAAAAA000006 → AAAAAA 000006 (문자 접두 + 숫자)
 */
const formatMacForDisplay = (mac: string | null | undefined) => {
  const value = (mac ?? "").trim();
  if (!value) return "";
  const compact = value.replace(/[\s:\-]/g, "").toUpperCase();
  if (/^[0-9A-F]{12}$/.test(compact)) {
    return `${compact.slice(0, 6)} ${compact.slice(6)}`;
  }
  const parsed = parseSerialSequence(compact);
  if (parsed?.prefix) {
    return `${parsed.prefix} ${parsed.numberText}`;
  }
  return value;
};

/** 한글 IME 상태에서도 물리 키(영문) 기준으로 문자 추출 */
const physicalCharFromKeyboardEvent = (e: {
  code: string;
  key: string;
  shiftKey: boolean;
}): string | null => {
  const { code } = e;
  if (code.startsWith("Key") && code.length === 4) {
    const letter = code.slice(3);
    return e.shiftKey ? letter.toUpperCase() : letter.toLowerCase();
  }
  if (code.startsWith("Digit") && code.length === 6) {
    const digit = code.slice(5);
    if (!e.shiftKey) return digit;
    const shifted: Record<string, string> = {
      "1": "!",
      "2": "@",
      "3": "#",
      "4": "$",
      "5": "%",
      "6": "^",
      "7": "&",
      "8": "*",
      "9": "(",
      "0": ")",
    };
    return shifted[digit] ?? digit;
  }
  if (code.startsWith("Numpad") && code.length === 7) {
    const digit = code.slice(6);
    if (/^\d$/.test(digit)) return digit;
  }
  if (code === "Minus" || code === "NumpadSubtract") return "-";
  if (code === "Equal") return e.shiftKey ? "+" : "=";
  if (code === "Period" || code === "NumpadDecimal") return ".";
  if (code === "Slash") return e.shiftKey ? "?" : "/";
  // 이미 영문/숫자면 key 사용
  if (e.key.length === 1 && /[A-Za-z0-9\-_.]/.test(e.key)) return e.key;
  return null;
};

/** 바코드 스캔값: 한글 자모 → 영문 QWERTY 복원 후 대문자 */
const normalizeBarcodeScan = (raw: string) =>
  convertEn(raw.replace(/\s/g, "")).trim().toUpperCase();

const waitUntilReadableUnlocked = async (
  port: SerialPort,
  timeoutMs = 800,
): Promise<boolean> => {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    if (!port.readable?.locked) return true;
    await new Promise((r) => setTimeout(r, 20));
  }
  return !port.readable?.locked;
};

const ScsKeyInject: React.FC<{ menuTitle: string }> = ({ menuTitle }) => {
  const canWrite = useCanWrite();
  const { workEnvironment } = useWorkEnvironment();
  const workEnvironmentRef = useRef(workEnvironment);
  useEffect(() => {
    workEnvironmentRef.current = workEnvironment;
  }, [workEnvironment]);
  const serialPortRef = useRef<SerialPort | null>(null);
  const serialMonitorReaderRef =
    useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);
  const serialReceiveRef =
    useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const rxBufferRef = useRef<number[]>([]);
  const lastA2SentAtRef = useRef(0);
  /** 동일 A1 대기 구간에서 안내 로그 1회만 */
  const a1AnnouncedRef = useRef(false);
  const logScrollRef = useRef<HTMLDivElement>(null);
  const scanInputRef = useRef<HTMLInputElement>(null);
  const scanBufferRef = useRef("");
  const groupIdRef = useRef(0);
  const activeGroupIdRef = useRef<number | null>(null);

  const [searchSerial, setSearchSerial] = useState("");
  const [selectedTerminalGroupId, setSelectedTerminalGroupId] = useState<
    string | null
  >(null);
  const [terminalGroups, setTerminalGroups] = useState<TerminalGroupOption[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [terminal, setTerminal] = useState<TerminalInjectInfo | null>(null);
  const terminalRef = useRef<TerminalInjectInfo | null>(null);
  const [pendingList, setPendingList] = useState<TerminalListRow[]>([]);
  const [anchorRow, setAnchorRow] = useState<TerminalListRow | null>(null);
  const [queueFrom, setQueueFrom] = useState("");
  const queueFromRef = useRef("");
  /** 조회 시 고정한 범위 기준 S/N (주입 후 다음 이동 시에도 유지) */
  const queueRangeAnchorRef = useRef("");
  const lastCompletedSerialRef = useRef<string | null>(null);
  /** 이미 주입완료된 단말 재주입 시: mac → serial 순서 기억 */
  const reinjectPhaseRef = useRef<"mac" | "serial" | null>(null);

  const [isPortOpen, setIsPortOpen] = useState(false);
  /** 키 주입(S/N까지) 성공 후 시리얼 자동 해제 */
  const [autoCloseAfterInject, setAutoCloseAfterInject] = useState(
    readAutoCloseAfterInjectPref,
  );
  const autoCloseAfterInjectRef = useRef(autoCloseAfterInject);
  const openPortRef = useRef<() => Promise<void>>(async () => { });
  const closePortRef = useRef<() => Promise<void>>(async () => { });
  const isPortOpenRef = useRef(false);
  const [a1Listening, setA1Listening] = useState(false);
  const [a1Ready, setA1Ready] = useState(false);
  const [logGroups, setLogGroups] = useState<LogGroup[]>([]);
  const [queueDescending, setQueueDescending] = useState(false);
  /** A1 콜백 stale closure 방지: 최신 오름/내림 고정 */
  const queueDescendingRef = useRef(false);
  const selectedTerminalGroupIdRef = useRef<string | null>(null);
  const loadInjectQueueRef = useRef<
    (
      fromSpSerialNo: string,
      options?: { silent?: boolean; advanceToNext?: boolean },
    ) => Promise<void>
  >(async () => { });
  const onRxChunkRef = useRef<(bytes: number[]) => void>(() => { });
  const [sequenceGap, setSequenceGap] = useState<SequenceSkipNotice | null>(
    null,
  );
  const [gapModal, setGapModal] = useState<SequenceSkipNotice | null>(null);
  const [reinjectPhase, setReinjectPhase] = useState<"mac" | "serial" | null>(
    null,
  );

  useEffect(() => {
    terminalRef.current = terminal;
  }, [terminal]);

  useEffect(() => {
    autoCloseAfterInjectRef.current = autoCloseAfterInject;
  }, [autoCloseAfterInject]);

  useEffect(() => {
    isPortOpenRef.current = isPortOpen;
  }, [isPortOpen]);

  useEffect(() => {
    queueDescendingRef.current = queueDescending;
  }, [queueDescending]);

  useEffect(() => {
    selectedTerminalGroupIdRef.current = selectedTerminalGroupId;
  }, [selectedTerminalGroupId]);

  const activeSendPhase = useMemo((): SendTarget => {
    if (!terminal) return "mac";
    const next = resolveNextInjectType(terminal);
    if (next) return next;
    // 이미 완료된 단말: 재주입 단계 (기본 MAC부터)
    return reinjectPhase === "serial" ? "serial" : "mac";
  }, [reinjectPhase, terminal]);

  const resetReinjectPhase = () => {
    reinjectPhaseRef.current = null;
    setReinjectPhase(null);
  };

  const appendToActiveGroup = (text: string, tone: LogTone = "default") => {
    const gid = activeGroupIdRef.current;
    if (gid == null) return;
    const time = getFormattedDate();
    setLogGroups((prev) =>
      prev.map((g) =>
        g.id === gid ? { ...g, lines: [...g.lines, { time, text, tone }] } : g,
      ),
    );
  };

  const startLogGroup = (type: LogGroupType, title: string) => {
    const id = ++groupIdRef.current;
    activeGroupIdRef.current = id;
    const time = getFormattedDate();
    setLogGroups((prev) => [
      ...prev.slice(-30),
      { id, type, title, time, lines: [] },
    ]);
    return id;
  };

  const endActiveGroup = () => {
    activeGroupIdRef.current = null;
  };

  const logSystem = (text: string, tone: LogTone = "default") => {
    if (activeGroupIdRef.current == null) startLogGroup("system", "시스템");
    appendToActiveGroup(text, tone);
    endActiveGroup();
  };

  useEffect(() => {
    const el = logScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [logGroups]);

  const resetA1Announce = () => {
    a1AnnouncedRef.current = false;
    setA1Ready(false);
  };

  useEffect(() => {
    return () => {
      void closePort();
    };
  }, []);

  useEffect(() => {
    axiosInstance
      .get("/api/v1/terminal/group/list")
      .then((res) => {
        const list = (res.data?.data ?? []) as TerminalGroupOption[];
        setTerminalGroups(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        console.error(err);
        setTerminalGroups([]);
      });
  }, []);

  /** 바코드 스캐너: 포커스 없이 숨김 입력에 흡수 (Enter 시 조회) */
  useEffect(() => {
    const el = scanInputRef.current;
    if (!el) return;
    const focusScan = () => {
      const active = document.activeElement;
      if (
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement
      ) {
        if (active.dataset.scanCapture !== "true") {
          return;
        }
      }
      el.focus({ preventScroll: true });
    };
    focusScan();
    const onBlur = () => window.setTimeout(focusScan, 80);
    el.addEventListener("blur", onBlur);
    return () => el.removeEventListener("blur", onBlur);
  }, []);

  const applyTerminalFromRow = (row: TerminalListRow) => {
    const info = mapListRowToInjectInfo(row);
    if (!info.macAddress?.trim()) {
      toast.warn(`MAC 미등록: ${info.spSerialNo}`);
    }
    resetReinjectPhase();
    setTerminal(info);
    resetA1Announce();
  };

  const syncSearchFieldsFromSerial = (full: string) => {
    setSearchSerial(full.trim().toUpperCase());
  };

  const showSequenceGap = (
    notice: SequenceSkipNotice | null,
    options?: { silent?: boolean },
  ) => {
    setSequenceGap(notice);
    if (notice && !options?.silent) {
      setGapModal(notice);
    }
  };

  const loadInjectQueue = async (
    fromSpSerialNo: string,
    options?: { silent?: boolean; advanceToNext?: boolean },
  ) => {
    const advance = options?.advanceToNext ?? false;
    // 시리얼 RX 콜백 stale closure 방지: 정렬/그룹은 항상 ref 최신값
    const descending = queueDescendingRef.current;
    const groupIdRaw = selectedTerminalGroupIdRef.current;
    const groupIdNum = groupIdRaw ? Number(groupIdRaw) : NaN;
    const hasGroup = Number.isFinite(groupIdNum) && groupIdNum > 0;

    const inputSn = fromSpSerialNo.trim().toUpperCase();
    // 새 조회일 때만 범위 기준 S/N 갱신. 주입 후 다음 이동 시에는 초기 조회 기준 유지
    if (!advance && inputSn) {
      queueRangeAnchorRef.current = inputSn;
    }
    const q =
      advance && queueRangeAnchorRef.current
        ? queueRangeAnchorRef.current
        : inputSn;

    if (!q && !hasGroup) {
      if (!options?.silent) {
        toast.error("S/N을 입력하세요.");
      }
      return;
    }
    if (q) {
      queueFromRef.current = q;
      setQueueFrom(q);
      if (!advance) {
        syncSearchFieldsFromSerial(q);
      }
    }
    setLoading(true);
    resetA1Announce();
    try {
      const queueParams: Record<string, string | number | boolean> = {
        descending,
        currentPageNo: 1,
        recordsPerPage: INT_MAX,
      };
      if (q) {
        queueParams.fromSpSerialNo = q;
      }
      if (hasGroup) {
        queueParams.terminalGroupId = groupIdNum;
      }
      applyWorkEnvironmentToParams(queueParams, workEnvironmentRef.current);

      let anchor: TerminalListRow | null = null;
      let list: TerminalListRow[] = [];

      if (q) {
        const listParams: Record<string, string | number> = {
          spSerialNo: q,
          currentPageNo: 1,
          recordsPerPage: 1,
        };
        applyWorkEnvironmentToParams(listParams, workEnvironmentRef.current);
        const [anchorRes, queueRes] = await Promise.all([
          axiosInstance.get("/api/v1/terminal/list", {
            params: listParams,
          }),
          axiosInstance.get("/api/v1/terminal/inject-queue", {
            params: queueParams,
          }),
        ]);

        const anchorList = (
          anchorRes.data?.data as { list?: TerminalListRow[] }
        )?.list;
        anchor = anchorList?.[0] ?? null;
        if (!anchor) {
          setTerminal(null);
          setAnchorRow(null);
          setPendingList([]);
          if (!options?.silent) {
            toast.error(`등록되지 않은 S/N: ${q}`);
          }
          return;
        }
        if (
          isWorkEnvironmentModelMismatch(anchor, workEnvironmentRef.current)
        ) {
          setTerminal(null);
          setAnchorRow(null);
          setPendingList([]);
          if (!options?.silent) {
            toast.error(WORK_ENV_MODEL_MISMATCH_MSG);
          }
          return;
        }

        const data = queueRes.data?.data as
          | { list?: TerminalListRow[]; totalCount?: number }
          | undefined;
        const prefix = parseSerialSequence(q)?.prefix ?? "";
        list = filterRowsByPrefix(data?.list ?? [], prefix);

        if (hasGroup && !list.some((row) => row.spSerialNo === q)) {
          setTerminal(null);
          setAnchorRow(null);
          setPendingList([]);
          if (!options?.silent) {
            toast.error(`선택한 Note에 없는 S/N: ${q}`);
          }
          return;
        }
      } else {
        const queueRes = await axiosInstance.get(
          "/api/v1/terminal/inject-queue",
          { params: queueParams },
        );
        const data = queueRes.data?.data as
          | { list?: TerminalListRow[]; totalCount?: number }
          | undefined;
        list = data?.list ?? [];
        const ordered = sortSerialRows(list, descending);
        anchor = ordered[0] ?? null;
        if (!anchor) {
          setTerminal(null);
          setAnchorRow(null);
          setPendingList([]);
          if (!options?.silent) {
            toast.error("선택한 Note에 등록된 단말기가 없습니다.");
          }
          return;
        }
        queueFromRef.current = anchor.spSerialNo;
        queueRangeAnchorRef.current = anchor.spSerialNo;
        setQueueFrom(anchor.spSerialNo);
        syncSearchFieldsFromSerial(anchor.spSerialNo);
      }

      setAnchorRow(anchor);
      const navForWork = buildNavQueue(anchor, list);
      const orderedNavForWork = sortSerialRows(navForWork, descending);
      const pendingCount = countPendingInject(orderedNavForWork);
      setPendingList(list);

      let workRow = anchor;
      if (advance) {
        const completedSn = lastCompletedSerialRef.current;
        workRow =
          findNextIncompleteRow(orderedNavForWork, completedSn) ??
          findNextIncompleteRow(orderedNavForWork, null) ??
          anchor;
      }
      applyTerminalFromRow(workRow);
      if (workRow?.spSerialNo) {
        queueFromRef.current = workRow.spSerialNo;
        setQueueFrom(workRow.spSerialNo);
        syncSearchFieldsFromSerial(workRow.spSerialNo);
      }

      const orderedDisplay = orderedNavForWork;
      const workIdx = orderedDisplay.findIndex(
        (row) => row.spSerialNo === workRow.spSerialNo,
      );
      const nextInList =
        workIdx >= 0 ? orderedDisplay[workIdx + 1] : orderedDisplay[0];
      const listGapNotice = nextInList
        ? buildSequenceSkipNotice(
          workRow.spSerialNo,
          nextInList.spSerialNo,
          orderedDisplay,
        )
        : null;

      if (advance && lastCompletedSerialRef.current) {
        const advanceGapNotice = buildSequenceSkipNotice(
          lastCompletedSerialRef.current,
          workRow.spSerialNo,
          orderedNavForWork,
        );
        showSequenceGap(advanceGapNotice || listGapNotice, {
          silent: options?.silent,
        });
      } else {
        showSequenceGap(listGapNotice, { silent: options?.silent });
      }

      if (!options?.silent) {
        const lookupLabel = q || "Note";
        const anchorDone = isInjectWorkComplete(anchor);
        const orderLabel = descending ? "내림차순" : "오름차순";
        if (anchorDone && pendingCount === 0 && !advance) {
          toast.info(`검색 ${lookupLabel} 주입완료 · 대기 단말 없음`);
        } else if (advance) {
          toast.success(
            `다음 작업 ${workRow.spSerialNo} · 목록 ${orderedNavForWork.length}건 · 대기 ${pendingCount}건 (${orderLabel})`,
          );
        } else if (anchorDone) {
          toast.success(
            `검색 ${lookupLabel} · 주입완료 · 목록 ${orderedNavForWork.length}건 · 대기 ${pendingCount}건 (${orderLabel})`,
          );
        } else {
          toast.success(
            `검색 ${lookupLabel} · 작업대상 ${workRow.spSerialNo} · 목록 ${orderedNavForWork.length}건 · 대기 ${pendingCount}건 (${orderLabel})`,
          );
        }
      }
    } catch (e) {
      toast.error("주입 대기 목록 검색 실패");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  loadInjectQueueRef.current = loadInjectQueue;

  const fetchTerminal = () => {
    const q = searchSerial.trim();
    if (!q && !selectedTerminalGroupId) {
      toast.error("S/N을 입력하세요.");
      return;
    }
    void loadInjectQueueRef.current(q ? normalizeBarcodeScan(q) : "", {
      advanceToNext: false,
    });
  };

  const onBarcodeScan = (raw: string) => {
    const v = normalizeBarcodeScan(raw);
    if (!v) return;
    syncSearchFieldsFromSerial(v);
    lastCompletedSerialRef.current = null;
    setSequenceGap(null);
    setGapModal(null);
    void loadInjectQueueRef.current(v, { advanceToNext: false });
  };

  const patchInjectionStatus = async (
    type: "serial" | "mac",
    success: boolean,
    term: TerminalInjectInfo,
  ) => {
    const payload: Record<string, unknown> = {
      terminalId: term.terminalId,
      lookupSpSerialNo: term.spSerialNo,
      message:
        type === "serial"
          ? `S/N 주입 ${success ? "성공" : "실패"}(${term.spSerialNo})`
          : `MAC 주입 ${success ? "성공" : "실패"}(${term.macAddress})`,
    };
    if (type === "serial") {
      payload.spSerialInjectionYn = success ? "Y" : "N";
    } else {
      payload.macInjectionYn = success ? "Y" : "N";
    }
    const res = await axiosInstance.patch("/api/v1/terminal/update", payload);
    if (res.data?.code !== "200") {
      throw new Error(res.data?.msg || "서버 갱신 실패");
    }
  };

  const refreshTerminalState = (patch: Partial<TerminalInjectInfo>) => {
    setTerminal((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      resetA1Announce();
      return next;
    });
  };

  const sendA2Payload = async (port: SerialPort, payload: string) => {
    const writer = port.writable?.getWriter();
    if (!writer) throw new Error("쓰기 스트림을 가져올 수 없습니다");
    try {
      const packet = buildStxEtxAsciiLenPacket(0xa2, payload);
      await writer.write(packet);
      appendToActiveGroup(`▶ A2 전송  payload="${payload}"`, "default");
      appendToActiveGroup(`   PACKET ${bytesToHex(packet)}`, "detail");
    } finally {
      writer.releaseLock();
    }
  };

  interface ReadA3Result {
    ok: boolean;
    ascii?: string;
    error?: string;
  }

  const tryReadA3 = async (
    port: SerialPort,
    expectPayload: string,
  ): Promise<ReadA3Result> => {
    try {
      const unlocked = await waitUntilReadableUnlocked(port, 1000);
      if (!unlocked) {
        appendToActiveGroup("✗ 스트림 lock 해제 타임아웃", "err");
        return { ok: false, error: "스트림 lock 해제 타임아웃" };
      }
      const parser = parseSpSnType1Step2();
      const a3 = await startReading(
        parser,
        port,
        8000,
        serialReceiveRef,
        abortControllerRef,
      );
      const ascii = a3
        .map((b) => (b >= 0x20 && b <= 0x7e ? String.fromCharCode(b) : "."))
        .join("");
      appendToActiveGroup(`◀ A3 수신  "${ascii}"`, "detail");
      if (ascii.includes(expectPayload.replace(/\s/g, ""))) {
        appendToActiveGroup("✓ 확인 일치", "ok");
        return { ok: true, ascii };
      }
      appendToActiveGroup(`✗ 응답 불일치 (기대: ${expectPayload})`, "err");
      return {
        ok: false,
        ascii,
        error: `응답 불일치 (수신: "${ascii}", 기대: "${expectPayload}")`,
      };
    } catch (e: any) {
      appendToActiveGroup("✗ A3 수신 없음/타임아웃", "err");
      return { ok: false, error: e?.message || "A3 수신 없음/타임아웃" };
    }
  };

  const runInject = async (
    type: "serial" | "mac",
    a1Frame?: number[],
    workTerminal?: TerminalInjectInfo,
  ): Promise<boolean> => {
    if (!canWrite) {
      toast.error("실행 권한이 없습니다.");
      return false;
    }
    const term = workTerminal ?? terminalRef.current;
    if (!term) {
      toast.error("단말기를 먼저 검색하세요.");
      return false;
    }
    const port = serialPortRef.current;
    if (!port) {
      toast.error("시리얼 포트를 연결하세요.");
      return false;
    }

    const payload =
      type === "serial"
        ? term.spSerialNo
        : (term.macAddress ?? "").replace(/\s/g, "").toUpperCase();

    if (!payload) {
      toast.error(type === "mac" ? "MAC이 없습니다." : "S/N이 없습니다.");
      return false;
    }

    const title =
      type === "serial" ? `${payload} · S/N 주입` : `${payload} · MAC 주입`;

    startLogGroup(type, title);
    const commLogs: string[] = [];
    if (a1Frame) {
      const a1Hex = bytesToHex(a1Frame);
      appendToActiveGroup(`◀ A1 수신  ${a1Hex}`, "default");
      commLogs.push(`◀ A1 수신  ${a1Hex}`);
    }

    await stopSerialRxMonitor(serialMonitorReaderRef);
    setA1Listening(false);

    try {
      await sendA2Payload(port, payload);
      commLogs.push(`▶ A2 전송  payload="${payload}"`);
      lastA2SentAtRef.current = Date.now();
      const a3Res = await tryReadA3(port, payload);
      if (a3Res.ascii) {
        commLogs.push(`◀ A3 수신  "${a3Res.ascii}"`);
      }
      const ok = a3Res.ok;
      const typeLabel = type === "serial" ? "S/N" : "MAC";
      const failReason = a3Res.error;

      await patchInjectionStatus(type, ok, term);
      if (ok) {
        if (type === "serial") {
          refreshTerminalState({ spSerialInjectionYn: "Y" });
        } else {
          refreshTerminalState({ macInjectionYn: "Y" });
        }
      }

      // ActionLog DB 적재
      void setActionLog({
        eventType: "KEY_INJECT",
        title: `${typeLabel} 주입 ${ok ? "성공" : "실패"} (${payload})`,
        serialNo: term.spSerialNo || term.serialNo || undefined,
        content: {
          terminalId: term.terminalId,
          spSerialNo: term.spSerialNo,
          macAddress: term.macAddress,
          internalModelName: term.internalModelName,
          externalModelName: term.externalModelName,
          injectType: type,
          payload,
          result: ok ? "SUCCESS" : "FAILURE",
          commLogs,
          failReason,
        },
      });

      endActiveGroup();
      return ok;
    } catch (err: any) {
      appendToActiveGroup(`✗ 전송 실패: ${String(err)}`, "err");
      const typeLabel = type === "serial" ? "S/N" : "MAC";
      void setActionLog({
        eventType: "KEY_INJECT",
        title: `${typeLabel} 주입 실패 (${payload})`,
        serialNo: term.spSerialNo || term.serialNo || undefined,
        content: {
          terminalId: term.terminalId,
          spSerialNo: term.spSerialNo,
          macAddress: term.macAddress,
          injectType: type,
          payload,
          result: "FAILURE",
          commLogs,
          failReason: String(err),
        },
      });
      endActiveGroup();
      return false;
    } finally {
      resetA1Announce();
      if (serialPortRef.current) startA1Monitor(true);
    }
  };

  const handleA1Frame = (frame: number[]) => {
    const workTerminal = terminalRef.current;
    if (!workTerminal) return;
    const now = Date.now();
    if (now - lastA2SentAtRef.current < 400) return;
    if (a1AnnouncedRef.current) return;

    a1AnnouncedRef.current = true;
    setA1Ready(true);

    void (async () => {
      const pendingType = resolveNextInjectType(workTerminal);
      const alreadyComplete = pendingType == null;
      // 이미 완료된 단말도 화면에 떠 있으면 MAC → S/N 순으로 재주입
      const injectType: SendTarget = alreadyComplete
        ? reinjectPhaseRef.current === "serial"
          ? "serial"
          : "mac"
        : pendingType;

      if (injectType === "mac") {
        logSystem(
          alreadyComplete
            ? `A1 수신 — ${workTerminal.spSerialNo} MAC 재주입 전송`
            : `A1 수신 — ${workTerminal.spSerialNo} MAC 자동 전송`,
          "ok",
        );
        const ok = await runInject("mac", frame, workTerminal);
        if (ok && alreadyComplete) {
          reinjectPhaseRef.current = "serial";
          setReinjectPhase("serial");
        }
        toast[ok ? "success" : "error"](
          ok
            ? alreadyComplete
              ? "MAC 재주입 전송 성공"
              : "MAC 자동 전송 성공"
            : alreadyComplete
              ? "MAC 재주입 전송 실패"
              : "MAC 자동 전송 실패",
        );
      } else {
        logSystem(
          alreadyComplete
            ? `A1 수신 — ${workTerminal.spSerialNo} S/N 재주입 전송`
            : `A1 수신 — ${workTerminal.spSerialNo} S/N 자동 전송`,
          "ok",
        );
        const ok = await runInject("serial", frame, workTerminal);
        if (ok) {
          lastCompletedSerialRef.current = workTerminal.spSerialNo;
          resetReinjectPhase();
          toast.success(
            alreadyComplete ? "S/N 재주입 전송 성공" : "S/N 자동 전송 성공",
          );
          await loadInjectQueueRef.current(
            queueRangeAnchorRef.current || queueFromRef.current,
            {
              silent: false,
              advanceToNext: true,
            },
          );
          if (autoCloseAfterInjectRef.current && serialPortRef.current) {
            await closePortRef.current();
            logSystem("주입 완료 · 시리얼 자동 해제", "ok");
            toast.info("주입 완료 · 시리얼 자동 해제");
          }
        } else {
          toast.error(
            alreadyComplete ? "S/N 재주입 전송 실패" : "S/N 자동 전송 실패",
          );
        }
      }
    })();
  };

  const onRxChunk = (bytes: number[]) => {
    rxBufferRef.current.push(...bytes);
    const { frames, remaining } = drainA1Frames(rxBufferRef.current);
    rxBufferRef.current = remaining;
    for (const frame of frames) {
      handleA1Frame(frame);
    }
  };
  onRxChunkRef.current = onRxChunk;

  const startA1Monitor = (silent = false) => {
    const port = serialPortRef.current;
    if (!port || serialMonitorReaderRef.current) return;
    rxBufferRef.current = [];
    // 항상 최신 onRxChunk 호출 (포트 연결 시점 스냅샷 방지)
    const ok = startSerialRxMonitor(
      port,
      (bytes) => onRxChunkRef.current(bytes),
      serialMonitorReaderRef,
    );
    if (ok) {
      setA1Listening(true);
      if (!silent) logSystem("A1 감시 시작", "detail");
    }
  };

  const stopA1Monitor = async () => {
    await stopSerialRxMonitor(serialMonitorReaderRef);
    setA1Listening(false);
    resetA1Announce();
  };

  const applyOpenedPort = (port: SerialPort, reconnect: boolean) => {
    serialPortRef.current = port;
    setIsPortOpen(true);
    resetA1Announce();
    startLogGroup("connect", reconnect ? "시리얼 재연결" : "시리얼 연결");
    appendToActiveGroup(
      reconnect
        ? "허용 포트 1개 · 선택 창 생략 · 115200 8N1"
        : "포트 연결 115200 8N1",
      "ok",
    );
    endActiveGroup();
    startA1Monitor();
    toast.success(
      reconnect
        ? "시리얼 재연결 · A1 감시 시작"
        : "시리얼 연결 · A1 감시 시작",
    );
    void setActionLog({
      eventType: "CONNECT",
      title: reconnect ? "시리얼 포트 재연결" : "시리얼 포트 연결",
      content: { baudRate: 115200, reconnect },
    });
  };

  const tryOpenPort = async (port: SerialPort) => {
    try {
      await port.open({ baudRate: 115200 });
      return true;
    } catch (err) {
      // 이미 열려 있으면 readable 이 존재할 수 있음
      if (port.readable) return true;
      throw err;
    }
  };

  /**
   * 시리얼 연결
   * - 이 origin에 허용된 포트 1개 → 선택 창 없이 바로 open
   * - 0개 또는 2개 이상 → requestPort() 선택 창
   * - 자동 재연결 실패 시 선택 창으로 폴백
   */
  const openPort = async () => {
    if (typeof navigator === "undefined" || !navigator.serial) {
      toast.error("이 브라우저는 Web Serial을 지원하지 않습니다. Chrome/Edge를 사용하세요.");
      return;
    }
    if (serialPortRef.current || isPortOpenRef.current) {
      toast.info("이미 시리얼이 연결되어 있습니다");
      return;
    }

    try {
      const granted = await navigator.serial.getPorts();
      let port: SerialPort | null = null;
      let reconnect = false;

      if (granted.length === 1) {
        port = granted[0];
        reconnect = true;
        try {
          await tryOpenPort(port);
        } catch {
          // 장치 분리·점유 등으로 실패 → 사용자가 다시 선택
          reconnect = false;
          port = await navigator.serial.requestPort();
          await tryOpenPort(port);
        }
      } else {
        // 미승인(0) 또는 허용 포트 여러 개 → 선택 창
        port = await navigator.serial.requestPort();
        await tryOpenPort(port);
      }

      applyOpenedPort(port, reconnect);
    } catch (err) {
      const name =
        err && typeof err === "object" && "name" in err
          ? String((err as { name: string }).name)
          : "";
      // 사용자 취소
      if (name === "NotFoundError" || name === "AbortError") {
        return;
      }
      toast.error(String(err));
    }
  };

  const closePort = async () => {
    await stopA1Monitor();
    const port = serialPortRef.current;
    if (port) {
      try {
        await port.close();
      } catch {
        /* ignore */
      }
    }
    serialPortRef.current = null;
    setIsPortOpen(false);
    logSystem("포트 연결 해제", "detail");
    void setActionLog({
      eventType: "CONNECT",
      title: "시리얼 포트 연결 해제",
    });
  };

  /** S/N 주입 성공 후 — 체크된 경우만 연결 해제 */
  const maybeAutoCloseAfterInject = async () => {
    if (!autoCloseAfterInjectRef.current) return;
    if (!serialPortRef.current) return;
    await closePort();
    logSystem("주입 완료 · 시리얼 자동 해제", "ok");
    toast.info("주입 완료 · 시리얼 자동 해제");
  };

  openPortRef.current = openPort;
  closePortRef.current = closePort;

  // F2: 시리얼 연결 / F3: 연결 해제
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.repeat) return;
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      if (e.key !== "F2" && e.key !== "F3") return;
      e.preventDefault();
      if (e.key === "F2") {
        if (isPortOpenRef.current) {
          toast.info("이미 시리얼이 연결되어 있습니다");
          return;
        }
        void openPortRef.current();
        return;
      }
      // F3
      if (!isPortOpenRef.current) {
        toast.info("연결된 시리얼이 없습니다");
        return;
      }
      void closePortRef.current();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const onSendMac = async () => {
    if (!canWrite) {
      toast.error("실행 권한이 없습니다.");
      return;
    }
    if (!a1Ready) {
      toast.error("단말기에서 A1을 먼저 보내주세요.");
      return;
    }
    if (!terminal?.macAddress?.trim()) {
      toast.error("등록된 MAC이 없습니다.");
      return;
    }
    const alreadyComplete = !!terminal && isInjectWorkComplete(terminal);
    if (isInjected(terminal?.macInjectionYn) && !alreadyComplete) {
      toast.warn(
        "이미 MAC 주입 완료 상태입니다. 단말이 맥(2번) 요청인지 확인하세요.",
      );
    }
    const ok = await runInject(
      "mac",
      undefined,
      terminalRef.current ?? undefined,
    );
    if (ok) {
      if (alreadyComplete || isInjected(terminal?.spSerialInjectionYn)) {
        reinjectPhaseRef.current = "serial";
        setReinjectPhase("serial");
      }
      toast.success(alreadyComplete ? "MAC 재주입 전송 성공" : "MAC 전송 성공");
    } else {
      toast.error(alreadyComplete ? "MAC 재주입 전송 실패" : "MAC 전송 실패");
    }
  };

  const onSendSerial = async () => {
    if (!canWrite) {
      toast.error("실행 권한이 없습니다.");
      return;
    }
    if (!a1Ready) {
      toast.error("단말기에서 A1을 먼저 보내주세요.");
      return;
    }
    const alreadyComplete = !!terminal && isInjectWorkComplete(terminal);
    if (isInjected(terminal?.spSerialInjectionYn) && !alreadyComplete) {
      toast.warn(
        "이미 S/N 주입 완료 상태입니다. 단말이 시리얼(3번) 요청인지 확인하세요.",
      );
    }
    const ok = await runInject(
      "serial",
      undefined,
      terminalRef.current ?? undefined,
    );
    if (ok) {
      if (terminalRef.current?.spSerialNo) {
        lastCompletedSerialRef.current = terminalRef.current.spSerialNo;
      }
      resetReinjectPhase();
      toast.success(alreadyComplete ? "S/N 재주입 전송 성공" : "S/N 전송 성공");
      await loadInjectQueueRef.current(
        queueRangeAnchorRef.current || queueFromRef.current,
        {
          silent: false,
          advanceToNext: true,
        },
      );
      await maybeAutoCloseAfterInject();
    } else {
      toast.error(alreadyComplete ? "S/N 재주입 전송 실패" : "S/N 전송 실패");
    }
  };

  const terminalWorkComplete = !!terminal && isInjectWorkComplete(terminal);
  const heroHint = (() => {
    if (!terminal) {
      return { kind: "idle" as const, text: "검색 후 전송할 값 미리보기" };
    }
    if (terminalWorkComplete) {
      const nextIncomplete = findNextIncompleteRow(
        sortSerialRows(
          ensureCurrentInQueue(buildNavQueue(anchorRow, pendingList), terminal),
          queueDescending,
        ),
        terminal.spSerialNo,
      );
      return {
        kind: "done" as const,
        text: nextIncomplete
          ? `재주입 가능 · 다음 ${nextIncomplete.spSerialNo}`
          : "재주입 가능",
      };
    }
    const pendingParts: string[] = [];
    if (!isInjected(terminal.spSerialInjectionYn)) pendingParts.push("S/N");
    if (!isInjected(terminal.macInjectionYn)) pendingParts.push("MAC");
    return {
      kind: "work" as const,
      need: pendingParts.length ? pendingParts.join("·") : "주입",
      text: "",
    };
  })();
  const canShowSendButtons = isPortOpen && !!terminal && canWrite;
  const displayQueue = useMemo(
    () =>
      sortSerialRows(
        ensureCurrentInQueue(
          buildDisplayQueue(anchorRow, pendingList),
          terminal,
        ),
        queueDescending,
      ),
    [anchorRow, pendingList, queueDescending, terminal],
  );
  const navQueue = useMemo(
    () =>
      sortSerialRows(
        ensureCurrentInQueue(buildNavQueue(anchorRow, pendingList), terminal),
        queueDescending,
      ),
    [anchorRow, pendingList, queueDescending, terminal],
  );
  const navQueueCount = navQueue.length;
  const pendingWorkCount = useMemo(
    () => countPendingInject(navQueue),
    [navQueue],
  );
  const currentNavIndex = useMemo(() => {
    if (!terminal) return -1;
    return navQueue.findIndex((row) => row.spSerialNo === terminal.spSerialNo);
  }, [navQueue, terminal]);
  const nextWorkSn =
    findNextIncompleteRow(navQueue, terminal?.spSerialNo ?? null)?.spSerialNo ??
    null;

  const queueScrollRef = useRef<HTMLDivElement | null>(null);

  const scrollQueueBy = (direction: -1 | 1) => {
    const el = queueScrollRef.current;
    if (!el) return;
    const step = Math.max(160, Math.floor(el.clientWidth * 0.7));
    el.scrollBy({ left: direction * step, behavior: "smooth" });
  };

  // 작업 대상이 바뀌면 목록에서 해당 배지가 보이도록 가로 스크롤
  useEffect(() => {
    const sn = terminal?.spSerialNo?.trim();
    const wrap = queueScrollRef.current;
    if (!sn || !wrap) return;
    const badge = wrap.querySelector<HTMLElement>(
      `[data-queue-sn="${CSS.escape(sn)}"]`,
    );
    badge?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [terminal?.spSerialNo, displayQueue.length]);

  useEffect(() => {
    const currentFrom =
      queueRangeAnchorRef.current.trim() || queueFromRef.current.trim();
    if (!currentFrom && !selectedTerminalGroupId) return;
    void loadInjectQueueRef.current(currentFrom, {
      silent: true,
      advanceToNext: false,
    });
  }, [queueDescending]);

  const goToQueueIndex = (index: number) => {
    const row = navQueue[index];
    if (!row) return;
    applyTerminalFromRow(row);
    const next = navQueue[index + 1];
    const gapNotice = next
      ? buildSequenceSkipNotice(row.spSerialNo, next.spSerialNo, navQueue)
      : null;
    showSequenceGap(gapNotice);
  };

  const goToPrevSerial = () => {
    if (currentNavIndex > 0) goToQueueIndex(currentNavIndex - 1);
  };

  const goToNextSerial = () => {
    if (currentNavIndex >= 0 && currentNavIndex < navQueue.length - 1) {
      goToQueueIndex(currentNavIndex + 1);
    }
  };

  const canGoPrev = currentNavIndex > 0;
  const canGoNext =
    currentNavIndex >= 0 && currentNavIndex < navQueue.length - 1;
  const macDone = !!terminal && isInjected(terminal.macInjectionYn);
  const serialDone = !!terminal && isInjected(terminal.spSerialInjectionYn);

  const queueSummaryText = queueFrom
    ? `${queueDescending ? "내림" : "오름"} · ${navQueueCount}건 · 대기 ${pendingWorkCount}`
    : null;

  const lineColor = (tone: LogTone) => {
    if (tone === "ok") return "#15803d";
    if (tone === "err") return "#dc2626";
    if (tone === "detail") return "#64748b";
    return "#1e293b";
  };

  return (
    <div className={classes.page}>
      <input
        ref={scanInputRef}
        type="text"
        data-scan-capture="true"
        className={classes.scanCapture}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        lang="en"
        inputMode="text"
        aria-hidden
        tabIndex={-1}
        onKeyDown={(e) => {
          // 한글 IME여도 물리 키코드로 영문 버퍼 구성
          if (e.key === "Enter") {
            e.preventDefault();
            const fromBuf = scanBufferRef.current;
            const fromVal = (e.currentTarget as HTMLInputElement).value;
            scanBufferRef.current = "";
            (e.currentTarget as HTMLInputElement).value = "";
            onBarcodeScan(fromBuf || fromVal);
            return;
          }
          if (e.key === "Backspace") {
            e.preventDefault();
            scanBufferRef.current = scanBufferRef.current.slice(0, -1);
            return;
          }
          const ch = physicalCharFromKeyboardEvent(e);
          if (!ch) return;
          e.preventDefault();
          scanBufferRef.current += ch;
        }}
      />
      <div className={classes.topArea}>
        <Box>
          <Text fz="xl" fw={700}>
            {menuTitle}
          </Text>
        </Box>

        <Paper p="md" withBorder radius="md">
          <Flex gap="sm" align="flex-end" wrap="wrap">
            <TextInput
              label={
                <Flex
                  justify="space-between"
                  align="center"
                  gap="sm"
                  wrap="wrap"
                  w="100%"
                >
                  <span>S/N 검색</span>
                  {queueSummaryText && (
                    <Text size="xs" c="dimmed" fw={500}>
                      {queueSummaryText}
                    </Text>
                  )}
                </Flex>
              }
              placeholder="바코드 스캔 또는 직접 입력"
              value={searchSerial}
              onChange={(e) =>
                setSearchSerial(e.currentTarget.value.toUpperCase())
              }
              onFocus={() => scanInputRef.current?.blur()}
              onBlur={() => {
                window.setTimeout(
                  () => scanInputRef.current?.focus({ preventScroll: true }),
                  120,
                );
              }}
              style={{ flex: 1, minWidth: 220 }}
              styles={{ label: { width: "100%" } }}
              size="md"
              onKeyDown={(e) => {
                if (e.key === "Enter") void fetchTerminal();
              }}
            />
            <div className={classes.searchActions}>
              <Button
                leftSection={<IconSearch size={18} />}
                onClick={() => void fetchTerminal()}
                loading={loading}
                size="md"
                className={classes.searchActionBtn}
              >
                검색
              </Button>
              <SegmentedControl
                value={queueDescending ? "desc" : "asc"}
                onChange={(value) => {
                  lastCompletedSerialRef.current = null;
                  setSequenceGap(null);
                  setGapModal(null);
                  setQueueDescending(value === "desc");
                }}
                data={[
                  { label: "오름차순", value: "asc" },
                  { label: "내림차순", value: "desc" },
                ]}
                color="blue"
                size="md"
                classNames={{
                  root: classes.sortControl,
                  label: classes.sortControlLabel,
                  control: classes.sortControlItem,
                }}
              />
            </div>
          </Flex>
        </Paper>

        {displayQueue.length > 0 && (
          <Paper p="sm" withBorder radius="md" className={classes.queueCompact}>
            <Flex
              justify="space-between"
              align="center"
              gap="sm"
              mb={6}
              wrap="wrap"
            >
              <Text size="sm" fw={600}>
                주입 목록
              </Text>
              <Text size="xs" c="dimmed">
                {navQueueCount}건 · 대기 {pendingWorkCount}건
              </Text>
            </Flex>
            <Flex align="center" gap={4} className={classes.queueScrollRow}>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                aria-label="이전 목록"
                onClick={() => scrollQueueBy(-1)}
                disabled={displayQueue.length === 0}
              >
                <IconChevronLeft size={16} />
              </ActionIcon>
              <div ref={queueScrollRef} className={classes.queueScroll}>
                {displayQueue.map((row) => {
                  const isCurrent = terminal?.spSerialNo === row.spSerialNo;
                  const isLookupAnchor = row.spSerialNo === queueFrom;
                  const isNextWork =
                    row.spSerialNo === nextWorkSn && !isCurrent;
                  const rowDone = isInjectWorkComplete(row);
                  return (
                    <span
                      key={row.spSerialNo}
                      data-queue-sn={row.spSerialNo}
                      className={classes.queueBadge}
                    >
                      <Badge
                        size="sm"
                        variant={isCurrent || rowDone ? "filled" : "light"}
                        color={
                          isCurrent
                            ? "red"
                            : rowDone
                              ? "green"
                              : isNextWork
                                ? "teal"
                                : isLookupAnchor
                                  ? "orange"
                                  : "blue"
                        }
                        style={{ cursor: "pointer" }}
                        onClick={() => applyTerminalFromRow(row)}
                        title={`S/N:${isInjected(row.spSerialInjectionYn) ? "Y" : "N"} MAC:${isInjected(row.macInjectionYn) ? "Y" : "N"}`}
                      >
                        {row.spSerialNo}
                        {isCurrent ? " · 작업" : ""}
                        {!isCurrent && isNextWork ? " · 다음" : ""}
                        {!isCurrent && !isNextWork && rowDone ? " · 완료" : ""}
                      </Badge>
                    </span>
                  );
                })}
              </div>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                aria-label="다음 목록"
                onClick={() => scrollQueueBy(1)}
                disabled={displayQueue.length === 0}
              >
                <IconChevronRight size={16} />
              </ActionIcon>
            </Flex>
          </Paper>
        )}

        {terminal && (
          <div className={classes.metaBar}>
            <span>
              <em>모델</em>
              {[terminal.internalModelName, terminal.externalModelName]
                .filter(Boolean)
                .join(" / ") || "—"}
            </span>
            <span>
              <em>S/N</em>
              {terminal.serialNo?.trim() || "(미등록)"}
            </span>
          </div>
        )}

        <div className={`${classes.hero} ${!terminal ? classes.heroIdle : ""}`}>
          <div className={classes.heroNavRow}>
            <ActionIcon
              className={classes.heroArrow}
              variant="light"
              color="teal"
              size={48}
              radius="xl"
              onClick={goToPrevSerial}
              disabled={!canGoPrev}
              aria-label="이전 S/N"
            >
              <IconChevronLeft size={32} stroke={2} />
            </ActionIcon>

            <div className={classes.heroMain}>
              <div className={classes.heroDualGrid}>
                <div
                  className={[
                    classes.heroPanel,
                    classes.heroPanelMac,
                    !terminal
                      ? classes.heroPanelIdle
                      : activeSendPhase === "mac"
                        ? classes.heroPanelActive
                        : macDone
                          ? classes.heroPanelDone
                          : classes.heroPanelPending,
                  ].join(" ")}
                >
                  <div className={classes.heroPanelLabel}>MAC</div>
                  <div className={classes.heroPanelValue}>
                    {formatMacForDisplay(terminal?.macAddress) || "—"}
                  </div>
                  <div className={classes.heroPanelStatus}>
                    {!terminal
                      ? "검색 필요"
                      : activeSendPhase === "mac"
                        ? terminalWorkComplete
                          ? "재주입"
                          : "주입 대상"
                        : macDone
                          ? "완료"
                          : "대기"}
                  </div>
                </div>

                <div
                  className={[
                    classes.heroPanel,
                    classes.heroPanelSerial,
                    !terminal
                      ? classes.heroPanelIdle
                      : activeSendPhase === "serial"
                        ? classes.heroPanelActive
                        : serialDone
                          ? classes.heroPanelDone
                          : classes.heroPanelPending,
                  ].join(" ")}
                >
                  <div className={classes.heroPanelLabel}>S/N</div>
                  <div className={classes.heroPanelValue}>
                    {terminal?.spSerialNo || "—"}
                  </div>
                  <div className={classes.heroPanelStatus}>
                    {!terminal
                      ? "검색 필요"
                      : activeSendPhase === "serial"
                        ? terminalWorkComplete
                          ? "재주입"
                          : "주입 대상"
                        : serialDone
                          ? "완료"
                          : "대기"}
                  </div>
                </div>
              </div>

              {terminal && (
                <div className={classes.heroFooter}>
                  {navQueueCount > 0 && currentNavIndex >= 0 && (
                    <Text size="sm" c="dimmed" fw={600}>
                      {currentNavIndex + 1} / {navQueueCount}
                    </Text>
                  )}
                  {sequenceGap && (
                    <Text
                      size="sm"
                      c={sequenceGap.kind === "missing" ? "red" : "teal"}
                      fw={700}
                    >
                      {sequenceGap.message}
                    </Text>
                  )}
                  {heroHint.kind === "work" ? (
                    <Badge
                      size="lg"
                      variant="filled"
                      color="red"
                      className={classes.heroNeedBadge}
                    >
                      {heroHint.need} 미완료
                    </Badge>
                  ) : (
                    <Text
                      size="sm"
                      fw={heroHint.kind === "done" ? 700 : 500}
                      c={heroHint.kind === "done" ? "orange" : "dimmed"}
                    >
                      {heroHint.text}
                    </Text>
                  )}
                </div>
              )}
            </div>

            <ActionIcon
              className={classes.heroArrow}
              variant="light"
              color="teal"
              size={48}
              radius="xl"
              onClick={goToNextSerial}
              disabled={!canGoNext}
              aria-label="다음 S/N"
            >
              <IconChevronRight size={32} stroke={2} />
            </ActionIcon>
          </div>
        </div>

        <div
          className={`${classes.a1Banner} ${a1Ready ? classes.a1Ready : classes.a1Wait}`}
        >
          {!isPortOpen
            ? "시리얼 연결 후 A1을 기다립니다"
            : a1Ready
              ? activeSendPhase === "mac"
                ? "A1 수신 — MAC 자동 전송 중"
                : "A1 수신 — S/N 자동 전송 중"
              : activeSendPhase === "mac"
                ? "MAC 전송 대기 — 단말에서 2번을 눌러주세요"
                : "S/N 전송 대기 — 단말에서 3번을 눌러주세요"}
        </div>
      </div>

      <Flex className={classes.actionBar} gap="sm" wrap="wrap" align="center">
        <Button
          onClick={() => void openPort()}
          disabled={isPortOpen}
          color="blue"
          size="md"
          title="단축키: F2"
        >
          시리얼 연결 (F2)
        </Button>
        <Button
          onClick={() => void closePort()}
          disabled={!isPortOpen}
          variant="default"
          size="md"
          title="단축키: F3"
        >
          연결 해제 (F3)
        </Button>
        <Checkbox
          label="주입 후 자동 해제"
          checked={autoCloseAfterInject}
          onChange={(e) => {
            const next = e.currentTarget.checked;
            setAutoCloseAfterInject(next);
            try {
              localStorage.setItem(
                AUTO_CLOSE_AFTER_INJECT_KEY,
                next ? "1" : "0",
              );
            } catch {
              /* ignore */
            }
          }}
          styles={{
            label: { fontSize: "0.875rem", cursor: "pointer" },
            body: { alignItems: "center" },
          }}
        />
        <Button
          onClick={() => void onSendMac()}
          disabled={!canShowSendButtons}
          color={activeSendPhase === "mac" ? "violet" : "gray"}
          variant={activeSendPhase === "mac" ? "filled" : "default"}
          size="lg"
          fw={700}
          style={{ opacity: activeSendPhase === "mac" ? 1 : 0.5 }}
        >
          MAC 전송
        </Button>
        <Button
          onClick={() => void onSendSerial()}
          disabled={!canShowSendButtons}
          color={activeSendPhase === "serial" ? "teal" : "gray"}
          variant={activeSendPhase === "serial" ? "filled" : "default"}
          size="lg"
          fw={700}
          style={{ opacity: activeSendPhase === "serial" ? 1 : 0.5 }}
        >
          S/N 전송
        </Button>

        <Text size="sm" c={isPortOpen ? "green" : "red"}>
          {isPortOpen
            ? a1Listening
              ? "연결됨 · A1 감시 중"
              : "연결됨"
            : "미연결"}
        </Text>
      </Flex>

      <div className={classes.logSection}>
        <div className={classes.logPanel} ref={logScrollRef}>
          <Text
            size="xs"
            fw={700}
            mb={8}
            style={{
              position: "sticky",
              top: 0,
              background: "#f1f5f9",
              zIndex: 1,
            }}
          >
            통신 로그
          </Text>
          {logGroups.length === 0 ? (
            <Text size="xs" c="dimmed">
              S/N 검색 → 시리얼 연결 → A1 수신 시 MAC 자동 → 다음 A1 시 S/N 자동
            </Text>
          ) : (
            logGroups.map((group) => {
              const style = GROUP_STYLE[group.type];
              return (
                <Box
                  key={group.id}
                  mb={8}
                  style={{
                    borderLeft: `4px solid ${style.border}`,
                    background: style.bg,
                    borderRadius: 6,
                    padding: "6px 8px",
                  }}
                >
                  <Text size="xs" fw={700} c={style.titleColor}>
                    [{group.time}] {group.title}
                  </Text>
                  {group.lines.map((line, i) => (
                    <div
                      key={i}
                      style={{
                        color: lineColor(line.tone),
                        wordBreak: "break-all",
                        fontSize: line.tone === "detail" ? 11 : 12,
                        paddingLeft: line.tone === "detail" ? 10 : 0,
                      }}
                    >
                      <span style={{ color: "#94a3b8", marginRight: 6 }}>
                        {line.time}
                      </span>
                      {line.text}
                    </div>
                  ))}
                </Box>
              );
            })
          )}
        </div>
      </div>

      <Modal
        opened={!!gapModal}
        onClose={() => setGapModal(null)}
        centered
        size="lg"
        radius="md"
        withCloseButton={false}
        closeOnClickOutside={false}
        closeOnEscape
        classNames={{ content: classes.gapModalContent }}
        title={
          <Flex align="center" gap="sm">
            <IconAlertTriangle
              size={28}
              color={gapModal?.kind === "missing" ? "#ca8a04" : "#0d9488"}
            />
            <Text fw={800} size="lg">
              {gapModal?.title ?? "시리얼 번호 확인"}
            </Text>
          </Flex>
        }
      >
        <Text className={classes.gapModalMessage} ta="center" my="xl">
          {gapModal?.message}
        </Text>
        <Flex justify="center">
          <Button
            size="lg"
            color={gapModal?.kind === "missing" ? "yellow" : "teal"}
            w={160}
            onClick={() => setGapModal(null)}
          >
            확인
          </Button>
        </Flex>
      </Modal>
    </div>
  );
};

export default ScsKeyInject;
